import { useEffect, useState, useRef } from 'react'
import { monitoringService, JobsPerformanceBatch } from '@/services/monitoring'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { RefreshCw } from 'lucide-react'

export interface JobsFilter {
  batchTimestamp: string
  filterType: 'hourly' | 'daily' | 'reprocess' | 'backfill' | 'email' | 'push' | 'total' | 'completed' | 'running' | 'pending' | 'errors'
  batchLabel: string
}

interface JobsPerformanceTableProps {
  onFilterSelect?: (filter: JobsFilter | null) => void
  activeFilter?: JobsFilter | null
  organizationId?: number
}

/**
 * Jobs Performance Table
 *
 * Displays hourly job performance metrics for the last 24 hours.
 * Shows breakdown by job kind (periodic, reprocess, backfill),
 * completion status, and duration metrics.
 * Clickable cells to filter the jobs table below.
 */
export function JobsPerformanceTable({ onFilterSelect, activeFilter, organizationId }: JobsPerformanceTableProps) {
  const [batches, setBatches] = useState<JobsPerformanceBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const hasFetched = useRef(false)
  const lastOrgId = useRef<number | undefined>(undefined)

  useEffect(() => {
    // Fetch on initial load or when organization changes
    if (!hasFetched.current || lastOrgId.current !== organizationId) {
      hasFetched.current = true
      lastOrgId.current = organizationId
      fetchPerformanceData()
    }
  }, [organizationId])

  const fetchPerformanceData = async () => {
    const startTime = Date.now()
    console.log('[LiveCollectionMonitor] Request started at', new Date(startTime).toISOString(), 'org:', organizationId)
    try {
      setLoading(true)
      setError(null)
      const response = await monitoringService.getJobsPerformanceByHour(24, undefined, undefined, organizationId)
      const endTime = Date.now()
      const duration = endTime - startTime
      console.log('[LiveCollectionMonitor] Response received at', new Date(endTime).toISOString(), `(${duration}ms)`, 'batches:', response.batches.length)
      setBatches(response.batches)
    } catch (err: any) {
      const endTime = Date.now()
      const duration = endTime - startTime
      console.error('[LiveCollectionMonitor] Failed at', new Date(endTime).toISOString(), `(${duration}ms)`, err)
      setError(err.response?.data?.detail || 'Failed to load performance data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading performance data...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
      </div>
    )
  }

  if (batches.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No job data available for the last 24 hours
      </div>
    )
  }

  const handleCellClick = (batch: JobsPerformanceBatch, filterType: JobsFilter['filterType']) => {
    if (!onFilterSelect) return
    if (!batch.batch_timestamp) return

    // Check if clicking the same filter - toggle off
    if (activeFilter?.batchTimestamp === batch.batch_timestamp && activeFilter?.filterType === filterType) {
      onFilterSelect(null)
      return
    }

    onFilterSelect({
      batchTimestamp: batch.batch_timestamp,
      filterType,
      batchLabel: batch.batch
    })
  }

  const isActive = (batch: JobsPerformanceBatch, filterType: JobsFilter['filterType']) => {
    return activeFilter?.batchTimestamp === batch.batch_timestamp && activeFilter?.filterType === filterType
  }

  const clickableClass = onFilterSelect ? 'cursor-pointer hover:underline' : ''
  const activeClass = 'ring-2 ring-offset-1 ring-primary rounded px-1'

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[70px] bg-muted/50">Batch</TableHead>
            <TableHead className="text-center w-[55px]" title="Integrations collected every hour">Hourly</TableHead>
            <TableHead className="text-center w-[50px]" title="Integrations collected once per day at optimal hour">Daily</TableHead>
            <TableHead className="text-center w-[70px]">Reprocess</TableHead>
            <TableHead className="text-center w-[55px]">Backfill</TableHead>
            <TableHead className="text-center w-[50px]">Async</TableHead>
            <TableHead className="text-center w-[50px]">Push</TableHead>
            <TableHead className="text-center w-[50px] bg-muted/50">Total</TableHead>
            <TableHead className="text-center w-[55px]">Pending</TableHead>
            <TableHead className="text-center w-[55px]">Running</TableHead>
            <TableHead className="text-center w-[50px]">Errors</TableHead>
            <TableHead className="text-center w-[65px]">Done</TableHead>
            <TableHead className="text-right w-[80px]">Records</TableHead>
            <TableHead className="text-right w-[50px]">Duration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {batches.slice(0, 24).map((batch) => (
            <TableRow key={batch.batch}>
              <TableCell className="font-mono text-xs bg-muted/50">{batch.batch}</TableCell>
              <TableCell className="text-center">
                {batch.hourly > 0 && (
                  <span
                    className={`text-blue-600 dark:text-blue-400 font-medium ${clickableClass} ${isActive(batch, 'hourly') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'hourly')}
                  >
                    {batch.hourly}
                  </span>
                )}
                {batch.hourly === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.daily > 0 && (
                  <span
                    className={`text-teal-600 dark:text-teal-400 font-medium ${clickableClass} ${isActive(batch, 'daily') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'daily')}
                  >
                    {batch.daily}
                  </span>
                )}
                {batch.daily === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.reprocess > 0 && (
                  <span
                    className={`text-purple-600 dark:text-purple-400 font-medium ${clickableClass} ${isActive(batch, 'reprocess') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'reprocess')}
                  >
                    {batch.reprocess}
                  </span>
                )}
                {batch.reprocess === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.backfill > 0 && (
                  <span
                    className={`text-orange-600 dark:text-orange-400 font-medium ${clickableClass} ${isActive(batch, 'backfill') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'backfill')}
                  >
                    {batch.backfill}
                  </span>
                )}
                {batch.backfill === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.email > 0 ? (
                  <span
                    className={`text-cyan-600 dark:text-cyan-400 font-medium ${clickableClass} ${isActive(batch, 'email') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'email')}
                  >
                    {batch.email}
                  </span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-center">
                {batch.push > 0 ? (
                  <span
                    className={`text-pink-600 dark:text-pink-400 font-medium ${clickableClass} ${isActive(batch, 'push') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'push')}
                  >
                    {batch.push}
                  </span>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell className="text-center bg-muted/50">
                <span
                  className={`font-medium ${clickableClass} ${isActive(batch, 'total') ? activeClass : ''}`}
                  onClick={() => handleCellClick(batch, 'total')}
                >
                  {batch.total_jobs}
                </span>
              </TableCell>
              <TableCell className="text-center">
                {batch.pending > 0 && (
                  <span
                    className={`text-slate-500 dark:text-slate-400 font-medium ${clickableClass} ${isActive(batch, 'pending') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'pending')}
                  >
                    {batch.pending}
                  </span>
                )}
                {batch.pending === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.in_progress > 0 && (
                  <span
                    className={`text-yellow-600 dark:text-yellow-400 font-medium ${clickableClass} ${isActive(batch, 'running') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'running')}
                  >
                    {batch.in_progress}
                  </span>
                )}
                {batch.in_progress === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.errors > 0 && (
                  <span
                    className={`text-red-600 dark:text-red-400 font-medium ${clickableClass} ${isActive(batch, 'errors') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'errors')}
                  >
                    {batch.errors}
                  </span>
                )}
                {batch.errors === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-center">
                {batch.completed > 0 && (
                  <span
                    className={`text-green-600 dark:text-green-400 font-medium ${clickableClass} ${isActive(batch, 'completed') ? activeClass : ''}`}
                    onClick={() => handleCellClick(batch, 'completed')}
                  >
                    {batch.completed}
                  </span>
                )}
                {batch.completed === 0 && <span className="text-muted-foreground">-</span>}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {batch.total_records > 0 ? batch.total_records.toLocaleString() : '-'}
              </TableCell>
              <TableCell className="text-right font-mono text-xs">
                {batch.total_min > 0
                  ? batch.total_min >= 60
                    ? `${Math.floor(batch.total_min / 60)}h${Math.round(batch.total_min % 60)}m`
                    : batch.total_min >= 1
                      ? `${Math.round(batch.total_min)}m`
                      : `${Math.round(batch.total_min * 60)}s`
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
